import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { isDerivedDose } from '~/domain/dose/derivedDose';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import {
    replanningCompareViewState,
    replanningQaMeasurementState,
    replanningRegisterTabState,
    replanningRunOutputsState,
    replanningRunRegistrationsState,
    replanningRunSummaryState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { detachedContourResultsState } from '~/state/atoms/detachedContourResultsAtom';
import { enqueueSnackbar } from '~/utils/notifications';
import { useResetReviewState } from './useResetReviewState';
import {
    landmarkPairListState,
    selectedLandmarkPairState,
} from '~/state/atoms/landmarkAtoms';

// Removes everything the run produced: its structure set, dose, registrations and the series they deformed.
export function useResetPropagation(): () => void {
    const intl = useIntl();
    const patient = useSelectedPatient();
    const runRegistrations = useRecoilValue(replanningRunRegistrationsState);
    const { propagatedUid, pendingPropagatedUid, propagatedDoseUid } =
        useRecoilValue(replanningRunOutputsState);
    const { deleteRegistration, deleteImage, deleteDose, deleteStructureSet } =
        useDeleteDataset();
    const setRegisterTab = useSetRecoilState(replanningRegisterTabState);
    const resetOutputs = useResetRecoilState(replanningRunOutputsState);
    const resetRunRegistrations = useResetRecoilState(
        replanningRunRegistrationsState
    );
    const resetRunSummary = useResetRecoilState(replanningRunSummaryState);
    const resetDetachedContourResults = useResetRecoilState(
        detachedContourResultsState
    );
    const resetQaMeasurement = useResetRecoilState(
        replanningQaMeasurementState
    );
    const resetCompareView = useResetRecoilState(replanningCompareViewState);
    // Landmarks are placed on this run's scans; a new run measures against new ones.
    const resetLandmarkPairs = useResetRecoilState(landmarkPairListState);
    const resetSelectedLandmarkPair = useResetRecoilState(
        selectedLandmarkPairState
    );
    const resetReviewState = useResetReviewState();

    return useCallback(() => {
        const producedSetUid = propagatedUid ?? pendingPropagatedUid;
        const producedSet = patient?.structureSets.find(
            (set) => set.dicomUid === producedSetUid
        );
        // A signed-off result is never removed behind the user's back: nothing is reset.
        if (producedSet?.isApproved) {
            enqueueSnackbar(
                intl.formatMessage({ id: 'replanning.summary.resetApproved' }),
                { variant: 'warning' }
            );
            return;
        }
        // Doses mapped or summed on the run's structures go with them.
        const derivedDoses =
            patient?.doses.filter(
                (dose) =>
                    isDerivedDose(dose) &&
                    !!producedSet &&
                    dose.structureSet === producedSet
            ) ?? [];
        for (const dose of derivedDoses) {
            deleteDose(dose);
        }
        if (producedSet) {
            deleteStructureSet(producedSet);
        }
        const producedDose = patient?.doses.find(
            (dose) => dose.dicomUid === propagatedDoseUid
        );
        if (producedDose) {
            deleteDose(producedDose);
        }
        for (const sopInstanceUid of runRegistrations) {
            const registration =
                patient?.getRegistrationWithSopInstanceUid(sopInstanceUid);
            if (registration) {
                deleteRegistration(registration);
            }
        }
        const removed = runRegistrations.filter(
            (sopInstanceUid) =>
                !patient?.getRegistrationWithSopInstanceUid(sopInstanceUid)
        );
        // A series stays while any registration that produced it survives.
        const derived =
            patient?.images.filter(
                (image) =>
                    removed.some((uid) => image.wasResampledBy(uid)) &&
                    image.resampledByRegistrationUids.every(
                        (uid) => !patient.getRegistrationWithSopInstanceUid(uid)
                    )
            ) ?? [];
        for (const image of derived) {
            deleteImage(image);
        }
        resetRunRegistrations();
        resetRunSummary();
        resetOutputs();
        resetDetachedContourResults();
        resetQaMeasurement();
        resetCompareView();
        resetLandmarkPairs();
        resetSelectedLandmarkPair();
        resetReviewState();
        setRegisterTab('process');
    }, [
        intl,
        patient,
        propagatedUid,
        pendingPropagatedUid,
        propagatedDoseUid,
        runRegistrations,
        deleteRegistration,
        deleteImage,
        deleteDose,
        deleteStructureSet,
        resetRunRegistrations,
        resetRunSummary,
        resetOutputs,
        resetDetachedContourResults,
        resetQaMeasurement,
        resetCompareView,
        resetLandmarkPairs,
        resetSelectedLandmarkPair,
        resetReviewState,
        setRegisterTab,
    ]);
}
